/**
 * The backend the tests run on, and the one a browser without IndexedDB falls back to.
 *
 * Two jobs, and they are the same job. A unit suite in Node needs somewhere to put a record, and a
 * private window with storage switched off needs the game to keep running rather than fail to
 * boot. Neither survives a reload, which is exactly what a fallback should not pretend to do.
 *
 * `faults` is what makes the guarantees testable. A crash between staging and the swap, and a
 * store that has run out of room, are both things that only ever happen on somebody else's machine
 * at the worst moment, so they are things a test has to be able to cause on demand.
 */

import { isStagingKey } from '@/save/saveBackendContract';
import type { SaveBackend } from '@/save/saveBackendContract';

/** Which call should throw, and with what. Absent means the backend behaves. */
export interface BackendFaults {
  /** Thrown by `stage`. Pass a `QuotaExceededError` to exercise the full-store path. */
  readonly stage?: () => Error;
  /** Thrown by `commit`, which is the crash the staged write exists to survive. */
  readonly commit?: () => Error;
}

export interface MemorySaveBackend extends SaveBackend {
  /** The store as it stands, for a test that wants to look rather than to load. */
  snapshot(): ReadonlyMap<string, unknown>;
  setFaults(faults: BackendFaults): void;
}

export function createMemorySaveBackend(faults: BackendFaults = {}): MemorySaveBackend {
  const records = new Map<string, unknown>();
  let active = faults;

  return {
    read(key: string): Promise<unknown> {
      return Promise.resolve(records.get(key) ?? null);
    },

    keys(): Promise<readonly string[]> {
      return Promise.resolve([...records.keys()].sort());
    },

    stage(key: string, record: unknown): Promise<void> {
      const fault = active.stage?.();
      if (fault !== undefined) return Promise.reject(fault);
      // Cloned on the way in, the way a structured clone into IndexedDB would be, so a test that
      // mutates what it stored does not reach back into the store.
      records.set(key, structuredClone(record));
      return Promise.resolve();
    },

    commit(stagingKey: string, liveKey: string): Promise<void> {
      const fault = active.commit?.();
      if (fault !== undefined) return Promise.reject(fault);

      const staged = records.get(stagingKey);
      if (staged === undefined) {
        return Promise.reject(new Error(`commit: nothing staged under ${stagingKey}`));
      }

      // One step, mirroring the single IndexedDB transaction: the live key never holds a partial
      // record and the staging key never outlives a successful swap.
      records.set(liveKey, staged);
      if (stagingKey !== liveKey) records.delete(stagingKey);
      return Promise.resolve();
    },

    remove(key: string): Promise<void> {
      records.delete(key);
      return Promise.resolve();
    },

    close(): void {
      // Nothing to release. Present so a caller never has to know which backend it holds.
    },

    snapshot(): ReadonlyMap<string, unknown> {
      return new Map(records);
    },

    setFaults(next: BackendFaults): void {
      active = next;
    },
  };
}

/** The store being full, as the browser reports it, for a test that wants that exact failure. */
export function quotaError(): Error {
  return new DOMException('quota exceeded', 'QuotaExceededError');
}

/** Every live key, staging records excluded. Used by tests asserting a sweep actually swept. */
export function liveKeys(backend: MemorySaveBackend): readonly string[] {
  return [...backend.snapshot().keys()].filter((key) => !isStagingKey(key));
}
